/**
 * Typed API client -- wraps fetch to the SocialScale REST API.
 *
 * Features:
 * - AbortController-based request timeout (30 s default)
 * - Structured error classes: NetworkError, ServerError, ValidationError, AuthError
 * - Response Content-Type checking
 * - Dynamic base URL from VITE_API_BASE env or current origin
 */

// ── Base URL (no hardcoded localhost) ────────────────────────────────────────

const BASE: string =
  import.meta.env.VITE_API_BASE ??
  `${window.location.origin}/api/v1`

const AUTH_TOKEN_STORAGE_KEY = 'socialscale.auth.token'
const AUTH_CHANGED_EVENT = 'socialscale:auth-changed'
let authTokenCache: string | null = null

function loadStoredAuthToken(): string | null {
  if (authTokenCache !== null) return authTokenCache
  try {
    authTokenCache = window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
    return authTokenCache
  } catch {
    return null
  }
}

/** Expose the resolved base URL so other parts of the app can display it. */
export function getApiBase(): string {
  return BASE
}

export function getAuthToken(): string | null {
  return loadStoredAuthToken()
}

export function setAuthToken(token: string): void {
  authTokenCache = token
  try {
    window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token)
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT))
  } catch {
    // Ignore storage failures (private mode / disabled storage).
  }
}

export function clearAuthToken(): void {
  authTokenCache = null
  try {
    window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT))
  } catch {
    // Ignore storage failures.
  }
}

export function getAuthChangedEventName(): string {
  return AUTH_CHANGED_EVENT
}

// ── Error classes ────────────────────────────────────────────────────────────

/** Base class for all API errors. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Thrown on network failures (DNS, CORS, offline, timeout). */
export class NetworkError extends ApiError {
  constructor(message: string, details?: unknown) {
    super(message, 0, 'NETWORK_ERROR', details)
    this.name = 'NetworkError'
  }
}

/** Thrown when the server returns 5xx. */
export class ServerError extends ApiError {
  constructor(message: string, status: number, details?: unknown) {
    super(message, status, 'SERVER_ERROR', details)
    this.name = 'ServerError'
  }
}

/** Thrown on 400 / 422 validation failures. */
export class ValidationError extends ApiError {
  constructor(message: string, status: number, details?: unknown) {
    super(message, status, 'VALIDATION_ERROR', details)
    this.name = 'ValidationError'
  }
}

/** Thrown on 401 / 403 auth failures. */
export class AuthError extends ApiError {
  constructor(message: string, status: number, details?: unknown) {
    super(message, status, 'AUTH_ERROR', details)
    this.name = 'AuthError'
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const DEFAULT_TIMEOUT_MS = 30_000

function classifyError(status: number, body: { error?: string; message?: string; detail?: unknown }): ApiError {
  const msg = body.error ?? body.message ?? `HTTP ${status}`

  if (status === 401 || status === 403) {
    return new AuthError(msg, status, body.detail)
  }
  if (status === 400 || status === 422) {
    return new ValidationError(msg, status, body.detail)
  }
  if (status >= 500) {
    return new ServerError(msg, status, body.detail)
  }
  return new ApiError(msg, status, 'API_ERROR', body.detail)
}

// ── Core request function ────────────────────────────────────────────────────

interface RequestOptions extends RequestInit {
  /** Request timeout in ms. Defaults to 30 000. */
  timeout?: number
}

async function request<T>(path: string, init?: RequestOptions): Promise<T> {
  const { timeout = DEFAULT_TIMEOUT_MS, ...fetchInit } = init ?? {}

  // Abort controller for timeout
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)

  // Merge caller signal with our timeout signal
  if (fetchInit.signal) {
    fetchInit.signal.addEventListener('abort', () => controller.abort())
  }

  try {
    const headers = new Headers(fetchInit.headers)
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    const token = loadStoredAuthToken()
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    const res = await fetch(`${BASE}${path}`, {
      headers,
      ...fetchInit,
      signal: controller.signal,
    })

    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }))
      throw classifyError(res.status, body)
    }

    // Verify the response is actually JSON before parsing
    const contentType = res.headers.get('Content-Type') ?? ''
    if (!contentType.includes('application/json')) {
      // Some endpoints return 204 No Content -- handle gracefully
      if (res.status === 204) {
        return undefined as unknown as T
      }
      // Attempt JSON parse anyway (some servers omit the header)
      try {
        return (await res.json()) as T
      } catch {
        throw new ApiError(
          `Expected JSON response but received ${contentType || 'unknown content type'}`,
          res.status,
          'INVALID_RESPONSE_TYPE',
        )
      }
    }

    return (await res.json()) as T
  } catch (err) {
    // Re-throw our own errors as-is
    if (err instanceof ApiError) throw err

    // AbortError from timeout
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new NetworkError(`Request timed out after ${timeout}ms`)
    }

    // Generic network failure (offline, DNS, CORS, etc.)
    throw new NetworkError(
      err instanceof Error ? err.message : 'Network request failed',
      err,
    )
  } finally {
    clearTimeout(timer)
  }
}

// ── Auth ───────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  name?: string | null
  role: string
}

export interface AuthResponse {
  token: string
  role: string
  expires_at?: string | null
  user?: AuthUser | null
}

export interface MeResponse {
  authenticated: boolean
  role: string
  user?: AuthUser | null
}

export const loginWithCredentials = (email: string, password: string) =>
  request<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })

export const registerWithCredentials = (name: string, email: string, password: string) =>
  request<AuthResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ name, email, password }),
  })

export const currentSession = () =>
  request<MeResponse>('/auth/me')

export const logoutSession = () =>
  request<{ ok: boolean }>('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({}),
  })

// ── Health ──────────────────────────────────────────────────────────────
export const health = () => request<{ status: string }>('/status')

// ── Inbox ───────────────────────────────────────────────────────────────
export const listThreads = (status = 'open', limit = 20) =>
  request<{ threads: Thread[]; total: number }>(`/inbox?status=${status}&limit=${limit}`)

export const triageThread = (id: string) =>
  request(`/inbox/${id}/triage`, { method: 'POST' })

export const suggestReply = (id: string) =>
  request<{ suggestions: string[] }>(`/inbox/${id}/reply`, { method: 'POST', body: JSON.stringify({}) })

// ── Trends ──────────────────────────────────────────────────────────────
export const listTrends = (limit = 10) =>
  request<{ signals: TrendSignal[]; total: number }>(`/trends?limit=${limit}`)

export const fetchTrends = () =>
  request('/trends/fetch', { method: 'POST' })

export const trendsDigest = () =>
  request<{ digest: string }>('/trends/digest')

export const trendsplan = (count = 5) =>
  request('/trends/plan', { method: 'POST', body: JSON.stringify({ count }) })

// ── Content / Drafts ────────────────────────────────────────────────────
export const listDrafts = (status?: string, limit = 20) =>
  request<{ drafts: Draft[]; total: number }>(`/content/drafts?${status ? `status=${status}&` : ''}limit=${limit}`)

export const listAssets = (limit = 50) =>
  request<{ assets: MediaAsset[]; total: number }>(`/assets?limit=${limit}`)

export const createDraft = (data: {
  content: string
  platforms: string[]
  brand_id?: string
  assets?: Array<{ asset_id: string; alt_text?: string }>
}) =>
  request<Draft>('/content/drafts', { method: 'POST', body: JSON.stringify(data) })

export const replaceDraftAssets = (
  id: string,
  assets: Array<{ asset_id: string; alt_text?: string }>,
) =>
  request<DraftAssetUpdate>(`/content/drafts/${id}/assets`, {
    method: 'PUT',
    body: JSON.stringify({ assets }),
  })

export const reviewDraft = (id: string) =>
  request(`/content/drafts/${id}/review`, { method: 'POST' })

export const scoreDraft = (id: string) =>
  request<{ score: number; breakdown: ScoreComponent[]; suggestions: string[] }>(`/content/drafts/${id}/score`)

// ── Ads ─────────────────────────────────────────────────────────────────
export const listCampaigns = (status?: string) =>
  request<{ campaigns: Campaign[]; total: number }>(`/ads/campaigns${status ? `?status=${status}` : ''}`)

export const createCampaign = (data: { goal: string; budget_daily_usd: number; platform?: string; audience_description?: string }) =>
  request<{ id: string; plan: CampaignPlan }>('/ads/campaigns', { method: 'POST', body: JSON.stringify(data) })

export const optimizeAds = (policy = 'moderate') =>
  request<{ actions: OptAction[] }>('/ads/optimize', { method: 'POST', body: JSON.stringify({ policy }) })

export const adsReport = (range = '7d') =>
  request<AdsReport>(`/ads/report?range=${range}`)

// ── Experiments ─────────────────────────────────────────────────────────
export const listExperiments = (status?: string) =>
  request<{ experiments: Exp[]; total: number }>(`/experiments${status ? `?status=${status}` : ''}`)

export const createExperiment = (data: { name: string; variant_draft_ids: string[]; metric?: string; hypothesis?: string; min_sample_size?: number }) =>
  request<{ id: string; variants_created: number }>('/experiments', { method: 'POST', body: JSON.stringify(data) })

export const getExperiment = (id: string) =>
  request<ExpDetail>(`/experiments/${id}`)

export const startExperiment = (id: string) =>
  request<{ ok: boolean; status: string }>(`/experiments/${id}/start`, { method: 'POST' })

export const concludeExperiment = (id: string) =>
  request<ConcludeResult>(`/experiments/${id}/conclude`, { method: 'POST' })

export const experimentResults = (id: string) =>
  request<{ results: Record<string, unknown>; status: string }>(`/experiments/${id}/results`)

export const listLearnings = (type?: string) =>
  request<{ learnings: Learning[]; total: number }>(`/experiments/learnings${type ? `?status=${type}` : ''}`)

export const analyticsGenome = (platform?: string) =>
  request<GenomeResponse>(`/analytics/genome${platform ? `?platform=${platform}` : ''}`)

// ── Analytics ────────────────────────────────────────────────────────────
export const analyticsOverview = (range = '7d') =>
  request<AnalyticsOverview>(`/analytics/overview?range=${range}`)

export const analyticsContent = (range = '7d') =>
  request(`/analytics/content?range=${range}`)

// ── Calendar ─────────────────────────────────────────────────────────────
export const calendarEvents = (range = 'week') =>
  request<{ events: CalendarEvent[]; range: string }>(`/calendar?range=${range}`)

// ── Agents ───────────────────────────────────────────────────────────────
export const agentsStatus = () =>
  request<{ fleet: AgentFleetItem[] }>('/agents/status')

export const agentLogs = (limit = 20) =>
  request<{ logs: AgentLog[]; total: number }>(`/agents/logs?limit=${limit}`)

export const startAgent = (agent_type: string) =>
  request('/agents/start', { method: 'POST', body: JSON.stringify({ agent_type }) })

export const stopAgent = (agent_type: string) =>
  request('/agents/stop', { method: 'POST', body: JSON.stringify({ agent_type }) })

// ── Governance ───────────────────────────────────────────────────────────
export const listPolicies = () =>
  request<{ policies: Policy[] }>('/governance/policies')

export const listApprovals = () =>
  request<{ approvals: Approval[]; total: number }>('/governance/approvals')

// ── Types ───────────────────────────────────────────────────────────────
export interface Thread {
  id: string
  platform: string
  category: string
  status: string
  sentiment: number
  last_message: string
  created_at: string
}

export interface TrendSignal {
  id: string
  topic: string
  source: string
  relevance_score: number
  velocity: number
  detected_at: string
}

export interface Draft {
  id: string
  brand_id?: string
  content: string
  platforms: string[]
  assets: DraftAsset[]
  status: string
  created_at: string
}

export interface DraftAsset {
  id: string
  asset_id: string
  asset_type: 'image' | 'video' | 'gif'
  url: string
  alt_text?: string | null
}

export interface DraftAssetUpdate {
  ok: boolean
  changed: boolean
  draft_id: string
  brand_id: string
  status: string
  assets: DraftAsset[]
  approval_invalidated: boolean
  cancelled_schedules: number
  url_refresh_only: boolean
}

export interface MediaAsset {
  id: string
  workspace_id: string
  brand_id: string
  asset_type: string
  source_url: string
  storage_key: string
  content_type: string
  size_bytes: number
  rights_status: string
  created_at: string
}

export interface Campaign {
  id: string
  name: string
  objective: string
  status: string
  daily_budget_usd: number
  metrics: { spend: number; roas: number; ctr: number; impressions: number }
}

export interface CampaignPlan {
  campaign_name: string
  objective: string
  bid_strategy: string
  placements: string[]
  estimated_reach: string
  estimated_clicks: string
}

export interface OptAction {
  campaign_id: string
  campaign_name: string
  action: string
  reason: string
  old_budget: number
  new_budget: number
}

export interface AdsReport {
  period: string
  total_spend: number
  total_impressions: number
  total_clicks: number
  avg_ctr: number
  avg_cpa: number
  avg_roas: number
  narrative: string
}

export interface Exp {
  id: string
  name: string
  status: string
  metric: string
  winner?: string
  lift?: number
  created_at?: string
}

export interface ExpVariant {
  id: string
  name: string
  is_control: boolean
  draft_id?: string
  metrics: { impressions?: number; engagements?: number; clicks?: number; saves?: number }
}

export interface ExpDetail extends Exp {
  hypothesis?: string
  started_at?: string
  ended_at?: string
  variants: ExpVariant[]
  results: Record<string, unknown>
}

export interface ConcludeResult {
  experiment_id: string
  conclusion: string
  winner?: string
  lift_percent: number
  confidence: number
  p_value: number
  explanation: string
  variant_win_probs: Record<string, number>
  learning_stored: boolean
  learning?: { learning_type: string; insight: string; confidence: number }
}

export interface Learning {
  id: string
  experiment_id?: string
  learning_type: string
  insight: string
  confidence: number
  applied_count: number
  created_at: string
}

export interface GenomeInsight {
  attribute: string
  value: string
  avg_performance: number
  sample_count: number
  lift_vs_average: number
}

export interface GenomeResponse {
  total_posts_analysed: number
  platform_filter: string
  insights: GenomeInsight[]
  summary: {
    best_hook_type?: { value: string; lift: number; samples: number }
    best_format?: { value: string; lift: number; samples: number }
    best_posting_hour?: { value: string; lift: number; samples: number }
  }
  note: string
}

export interface AnalyticsOverview {
  reach: number
  impressions: number
  engagement_rate: number
  follower_growth: number
  posts_published: number
  conversions: number
  conversion_value: number
  period: string
}

export interface ConversionEvent {
  id: string
  event_id: string
  event_name: string
  event_time: string
  value: number
  currency: string | null
  attribution: Record<string, string | null>
  browser_identifiers_present: boolean
  pii_fields_hashed: string[]
  custom_data: Record<string, unknown>
  delivery_status: string
  created_at: string
}

export const listConversions = (range = '7d') =>
  request<{
    range: string
    events: ConversionEvent[]
    total: number
    total_value: number
    capi: { external_delivery_allowed: boolean; block_reason: string | null }
  }>(`/analytics/conversions?range=${encodeURIComponent(range)}`)

export interface ScoreComponent {
  factor: string
  impact: number
  description: string
}

export interface CalendarEvent {
  id: string
  type: 'scheduled' | 'draft'
  platform?: string
  platforms?: string[]
  status?: string
  date?: string
  content_preview: string
}

export interface AgentFleetItem {
  agent_type: string
  info: {
    status: string
    tasks_today: number
    last_run?: string
  }
}

export interface AgentLog {
  id: string
  agent_type: string
  status: string
  completed_at?: string
  tasks_completed: number
  cost_usd: number
  error?: string
}

export interface Policy {
  id: string
  name: string
  policy_type: string
  enabled: boolean
  created_at: string
}

export interface Approval {
  id: string
  requester: string
  item_type: string
  item_id: string
  status: string
}

// ── Integrations / Credential Vault ─────────────────────────────────────────
export interface CredentialFieldDef {
  key: string
  label: string
  placeholder: string
  secret: boolean
  required: boolean
  help: string
}

export interface PlatformStatus {
  id: string
  label: string
  category: string
  color: string
  icon: string
  oauth_supported: boolean
  supports_multiple_accounts: boolean
  account_noun: string
  docs_url: string
  fields: CredentialFieldDef[]
  has_credentials: boolean
  credential_values: Record<string, string>
  updated_at: string | null
  connected_count: number
}

export const listIntegrations = () =>
  request<{ platforms: PlatformStatus[] }>('/integrations')

export const setCredentials = (platform: string, credentials: Record<string, string>) =>
  request<{ ok: boolean; message: string }>(`/integrations/${platform}`, {
    method: 'PUT',
    body: JSON.stringify({ credentials }),
  })

export const deleteCredentials = (platform: string) =>
  request<{ ok: boolean; message: string }>(`/integrations/${platform}`, {
    method: 'DELETE',
  })

export const testCredentials = (platform: string) =>
  request<{ platform: string; status: string; message: string }>(
    `/integrations/${platform}/test`,
  )

export const getOAuthUrl = (platform: string) =>
  request<{ platform: string; auth_url: string; state: string; redirect_uri: string }>(
    `/integrations/oauth/${platform}`,
  )

export const submitOAuthCallback = (platform: string, code: string, state: string) =>
  request<{
    ok: boolean
    account: { id: string; platform: string; display_name: string; platform_account_id: string }
    pages_hint: string | null
    message: string
  }>(`/integrations/oauth/${platform}/callback`, {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  })

export interface FacebookPage {
  id: string
  name: string
  category: string
  picture?: { data: { url: string } }
}

export const listFacebookPages = (accountId: string) =>
  request<{ pages: FacebookPage[]; user_token: string }>(
    `/integrations/oauth/facebook/pages?account_id=${accountId}`,
  )

export const connectFacebookPage = (page: {
  page_id: string; page_name: string; access_token: string; category?: string; avatar_url?: string
}) =>
  request<{ ok: boolean; account_id: string; page_name: string }>(
    '/integrations/oauth/facebook/pages',
    { method: 'POST', body: JSON.stringify(page) },
  )

// ── Connected Social Accounts ────────────────────────────────────────────────
export interface ConnectedAccount {
  id: string
  platform: string
  platform_label: string
  platform_color: string
  platform_icon: string
  display_name: string
  platform_account_id: string
  status: string
  avatar_url: string | null
  page_category: string | null
  connected_at: string
}

export const listConnectedAccounts = () =>
  request<{ accounts: ConnectedAccount[]; total: number }>('/integrations/accounts')

export const disconnectAccount = (id: string) =>
  request<{ ok: boolean; message: string }>(`/integrations/accounts/${id}`, { method: 'DELETE' })

// ── API Key Management ───────────────────────────────────────────────────────
export interface ApiToken {
  id: string
  label: string
  role: string
  token_preview: string
  created_at: string
  expires_at: string | null
}

export interface NewApiToken extends ApiToken {
  token: string
  warning: string
}

export const listApiKeys = () =>
  request<{ tokens: ApiToken[]; total: number }>('/keys')

export const createApiKey = (label: string, role = 'admin', expires_at?: string) =>
  request<NewApiToken>('/keys', {
    method: 'POST',
    body: JSON.stringify({ label, role, expires_at }),
  })

export const revokeApiKey = (id: string) =>
  request<{ ok: boolean; message: string }>(`/keys/${id}`, { method: 'DELETE' })

// ── Budget Intelligence ──────────────────────────────────────────────────────
export interface PlatformBudgetItem {
  platform: string
  color: string
  budget: number
  spent: number
  roi: number
  cpa: number
  conversions: number
}

export interface BudgetAlertItem {
  id: string
  type: string
  severity: 'info' | 'warning' | 'critical'
  platform: string
  message: string
  timestamp: string
}

export interface PacingPoint {
  day: string
  ideal: number
  actual: number
}

export interface BudgetOverviewResponse {
  platforms: PlatformBudgetItem[]
  totalBudget: number
  totalSpent: number
  remaining: number
  pacing: 'on_track' | 'overspending' | 'underspending'
  weightedRoi: number
  pacingData: PacingPoint[]
  alerts: BudgetAlertItem[]
  range: string
}

export const budgetOverview = (range = '30d') =>
  request<BudgetOverviewResponse>(`/ads/budget?range=${range}`)

// ── Crisis Monitoring ────────────────────────────────────────────────────────
export interface CrisisItemApi {
  id: string
  title: string
  severity: string
  platform: string
  description: string
  started_at: string
  resolved_at: string | null
}

export interface CrisisEventApi {
  id: string
  type: string
  message: string
  severity: string
  timestamp: string
}

export interface SentimentPointApi {
  date: string
  baseline: number
  current: number
}

export interface CrisisStatusResponse {
  level: 'normal' | 'watch' | 'warning' | 'critical' | 'emergency'
  active_count: number
  events_today: number
  avg_sentiment: number
  crises: CrisisItemApi[]
  timeline: CrisisEventApi[]
  sentiment: SentimentPointApi[]
}

export const crisisStatus = () =>
  request<CrisisStatusResponse>('/crisis/status')

export const pauseAllContent = () =>
  request<{ ok: boolean; paused_count: number; message: string }>(
    '/crisis/pause-all',
    { method: 'POST' },
  )

// ── Webhooks ─────────────────────────────────────────────────────────────────
export interface WebhookEndpointItem {
  id: string
  url: string
  events: string[]
  active: boolean
  failure_count: number
  created_at: string
  last_delivery: string | null
}

export interface WebhookDeliveryItem {
  id: string
  endpoint_id: string
  event_type: string
  status: string
  attempts: number
  response_status: number | null
  created_at: string
  delivered_at: string | null
}

export const listWebhookEndpoints = () =>
  request<{ endpoints: WebhookEndpointItem[] }>('/webhooks/endpoints')

export const createWebhookEndpoint = (data: { url: string; events: string[]; secret?: string }) =>
  request<{ id: string; url: string; events: string[]; active: boolean; created_at: string }>('/webhooks/endpoints', {
    method: 'POST',
    body: JSON.stringify(data),
  })

export const deleteWebhookEndpoint = (id: string) =>
  request<{ ok: boolean }>(`/webhooks/endpoints/${id}`, { method: 'DELETE' })

export const pingWebhookEndpoint = (id: string) =>
  request<{ ok: boolean; response_code: number; duration_ms: number }>(
    `/webhooks/test/${id}`,
    { method: 'POST' },
  )

export const webhookDeliveries = (endpointId: string) =>
  request<{ deliveries: WebhookDeliveryItem[]; total: number }>(
    `/webhooks/deliveries/${endpointId}`,
  )

// ── Compliance ───────────────────────────────────────────────────────────────
export interface ComplianceIssueItem {
  id: string
  severity: string
  rule: string
  description: string
  position: { start: number; end: number } | null
  suggestion: string | null
  auto_fixable: boolean
}

export interface ComplianceScanResult {
  id: string
  content: string
  platform: string
  industry: string
  score: number
  passed: boolean
  issues: ComplianceIssueItem[]
  scanned_at: string
}

export const complianceScan = (content: string, platform?: string, industry?: string) =>
  request<ComplianceScanResult>('/compliance/scan', {
    method: 'POST',
    body: JSON.stringify({ content, platform, industry }),
  })

export const complianceHistory = (limit = 20) =>
  request<{ scans: ComplianceScanResult[]; total: number }>(
    `/compliance/history?limit=${limit}`,
  )

// ── Campaign detail & actions ────────────────────────────────────────────────
export const getCampaign = (id: string) =>
  request<Campaign>(`/ads/campaigns/${id}`)

export const pauseCampaign = (id: string) =>
  request<{ ok: boolean }>(`/ads/campaigns/${id}/pause`, { method: 'POST' })

export const resumeCampaign = (id: string) =>
  request<{ ok: boolean }>(`/ads/campaigns/${id}/resume`, { method: 'POST' })

export const getCampaignCreatives = (id: string) =>
  request<{ creatives: Creative[] }>(`/ads/campaigns/${id}/creatives`)

// ── Governance actions ───────────────────────────────────────────────────────
export const togglePolicy = (id: string) =>
  request<{ ok: boolean; id: string; enabled: boolean }>(`/governance/policies/${id}/toggle`, { method: 'POST' })

export const approveRequest = (id: string) =>
  request<{ ok: boolean; id: string; status: string }>(`/governance/approvals/${id}/approve`, { method: 'POST' })

export const rejectRequest = (id: string) =>
  request<{ ok: boolean; id: string; status: string }>(`/governance/approvals/${id}/reject`, { method: 'POST' })

export const auditLog = (limit = 50, actor?: string) =>
  request<{ entries: AuditEntry[]; total: number }>(`/governance/audit?limit=${limit}${actor ? `&actor=${actor}` : ''}`)

// ── Draft actions ────────────────────────────────────────────────────────────
export const approveDraft = (id: string) =>
  request<{ ok: boolean; draft_id: string; status: string }>(`/content/drafts/${id}/approve`, { method: 'POST' })

export const draftVariants = (id: string) =>
  request<{ variants: Array<{ id: string; content: string }> }>(`/content/drafts/${id}/variants`, { method: 'POST' })

// ── Additional types ─────────────────────────────────────────────────────────
export interface AuditEntry {
  id: string
  actor: string
  action: string
  resource_type: string | null
  resource_id: string | null
  created_at: string
}

export interface Creative {
  id: string
  campaign_id: string
  name: string
  format: string
  status: string
  performance_score: number | null
  created_at: string
}
